import { config } from '@/config'

/**
 * Third-party analytics, injected at runtime only when the berth's runtime config enables them, so local and staging
 * traffic never reaches the production GA4 property or Ometria's production account.
 *
 *   gaMeasurementId   -> Google Analytics 4 (gtag.js); SPA page views are sent by trackPageView()
 *   ometriaAccountId  -> Ometria's tracker (cdn.ometria.com/tags/<id>.js): page views with a page type and store id,
 *                        identify() after a newsletter signup (so a subscriber's visits join their Ometria profile),
 *                        and Ometria's overlays, which the tracker delivers. Each berth has its own Ometria account
 *                        (the same id as its signup form's @account). Off where ometriaTracking=false.
 */

type Gtag = (...args: unknown[]) => void

interface OmetriaApi {
  init?: (pageType: string, pageData?: Record<string, unknown> | null, storeId?: string) => void
  identify?: (email: string, data?: Record<string, unknown>) => void
}

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: Gtag
    ometria?: OmetriaApi
  }
}

export type OmetriaPageType = 'homepage' | 'listing' | 'tutorial' | 'other'

let initialized = false
let lastTrackedPath: string | null = null
let ometriaReady = false
let pendingPage: { type: OmetriaPageType } | null = null
let storeId = 'default'

function injectScript(src: string, onLoad?: () => void): HTMLScriptElement {
  const script = document.createElement('script')
  script.async = true
  script.src = src
  if (onLoad) script.addEventListener('load', onLoad)
  document.head.appendChild(script)
  return script
}

export interface AnalyticsConfig {
  gaMeasurementId: string
  ometriaAccountId: string
  ometriaStoreId?: string
  ometriaTracking?: string
}

/** Tracking is on for this berth (an account id and not switched off). */
export const ometriaTrackingEnabled = (cfg: AnalyticsConfig = config) => !!cfg.ometriaAccountId && cfg.ometriaTracking !== 'false'

/** Idempotent; call once when the customer site mounts. */
export function initAnalytics(cfg: AnalyticsConfig = config): void {
  if (initialized || typeof window === 'undefined') return
  initialized = true

  if (cfg.gaMeasurementId) {
    window.dataLayer = window.dataLayer || []
    window.gtag = function gtag() {
      // gtag.js expects the Arguments object itself, not an array.
      window.dataLayer!.push(arguments)
    }
    window.gtag('js', new Date())
    // Page views are sent explicitly on every route change (see trackPageView).
    window.gtag('config', cfg.gaMeasurementId, { send_page_view: false })
    injectScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(cfg.gaMeasurementId)}`)
  }

  if (ometriaTrackingEnabled(cfg)) {
    storeId = cfg.ometriaStoreId || 'default'
    injectScript(`https://cdn.ometria.com/tags/${encodeURIComponent(cfg.ometriaAccountId)}.js`, () => {
      ometriaReady = true
      if (pendingPage) sendOmetriaPage(pendingPage.type)
    })
  }
}

function sendOmetriaPage(type: OmetriaPageType): void {
  try {
    window.ometria?.init?.(type, null, storeId)
  } catch {
    /* never let a third-party tag break the page */
  }
}

/**
 * A page view for a client-side navigation: GA4 page_view, and Ometria's init with the page type (the tag only sees
 * the first page load on its own). No-ops for whichever isn't enabled.
 */
export function trackPageView(path: string, ometriaType: OmetriaPageType = 'other'): void {
  if (path === lastTrackedPath) return
  lastTrackedPath = path

  window.gtag?.('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  })

  pendingPage = { type: ometriaType }
  if (ometriaReady) sendOmetriaPage(ometriaType)
}

/** Tell Ometria who this visitor is (after a newsletter signup), so their visits are linked to their profile. */
export function identifyVisitor(email: string, firstName?: string): void {
  try {
    window.ometria?.identify?.(email, firstName ? { firstname: firstName } : undefined)
  } catch {
    /* ignore */
  }
}

/** Ometria page type for a route id (see routes.ts). */
export function ometriaPageType(routeId: string | undefined): OmetriaPageType {
  switch (routeId) {
    case 'site-home':
      return 'homepage'
    case 'site-tutorial':
      return 'tutorial'
    case 'site-tutorials':
    case 'site-filtered':
    case 'site-category':
      return 'listing'
    default:
      return 'other'
  }
}

/** Test helper. */
export function resetAnalyticsForTests(): void {
  initialized = false
  lastTrackedPath = null
  ometriaReady = false
  pendingPage = null
  storeId = 'default'
}
